import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import type { FeedbackValue } from "./useFeedbackState";

const options: { key: FeedbackValue; label: string; dotClass: string; symbol: string }[] = [
  { key: "confirmed", label: "Confirmed", dotClass: "bg-[var(--orb-green)]", symbol: "✓" },
  { key: "not-found", label: "Not Found", dotClass: "bg-[var(--orb-gray)]", symbol: "✕" },
  { key: "not-sure", label: "Not Sure", dotClass: "bg-[var(--orb-amber)]", symbol: "?" },
];

interface OrbPopoverProps {
  currentFeedback: FeedbackValue;
  onSelect: (value: FeedbackValue) => void;
  onClose: () => void;
  anchorRect: DOMRect | null;
  containerRect: DOMRect | null;
}

export function OrbPopover({ currentFeedback, onSelect, onClose, anchorRect, containerRect }: OrbPopoverProps) {
  const popRef = useRef<HTMLDivElement>(null);

  // Position calculation
  let top = 0;
  if (anchorRect && containerRect) {
    const spaceBelow = containerRect.bottom - anchorRect.bottom;
    if (spaceBelow > 180) {
      top = anchorRect.bottom - containerRect.top + 4;
    } else {
      top = anchorRect.top - containerRect.top - 160;
    }
  }

  return (
    <>
      {/* Invisible overlay to catch outside taps */}
      <div className="fixed inset-0 z-50" onClick={onClose} />

      {/* Popover */}
      <div
        ref={popRef}
        className="absolute right-[14px] z-[60] bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.08)] overflow-hidden w-[180px] origin-top-right [animation:pop-in_0.2s_cubic-bezier(0.34,1.56,0.64,1)]"
        style={{ top }}
      >
        {options.map((opt, i) => (
          <button
            key={opt.key}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(opt.key);
            }}
            className={cn(
              "flex items-center gap-2.5 w-full px-3.5 py-3 text-sm font-semibold text-[var(--orb-text-primary)] transition-colors active:bg-[var(--orb-gray-bg)]",
              i > 0 && "border-t border-[var(--orb-border)]"
            )}
          >
            <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0", opt.dotClass)}>
              {opt.symbol}
            </span>
            <span>{opt.label}</span>
            {currentFeedback === opt.key && (
              <span className="ml-auto text-base text-[var(--orb-green)]">✓</span>
            )}
          </button>
        ))}
      </div>
    </>
  );
}
